import React, { useState } from "react";
import { ReactComponent as IconLeft } from "@assets/Icons/ArrowLeft.svg";
import { ReactComponent as ChevronDownSvg } from "@assets/Icons/IconChevron-Down.svg";
import { ReactComponent as IconKey } from "@assets/Icons/IconKey.svg";
import { ReactComponent as IconTag } from "@assets/Icons/IconTag.svg";
import { ReactComponent as IconSupport } from "@assets/Icons/IconSupport.svg";
import GenericButton from "@ui-components/Button/GenericButton";
import type { MenuItem } from "../MenuData";
import type { AdminSection } from "./AdminSections";
import {
  BADGE_INLINE,
  CHEVRON_ICON,
  CHROME_PADDING,
  DRILL_IN_LOGO,
  FOOTER_PADDING,
  FOOTER_STACK,
  LABEL_CLAMP,
  NAV_PADDING,
  NESTED_INDENT,
  ROW_GAP,
  ROW_ICON,
  ROW_PADDING,
  ROW_RADIUS,
  ROW_STACK,
  ROW_TEXT,
  SECTION_HEADER_PADDING,
  SECTION_HEADER_TEXT,
  SECTION_STACK,
} from "../sideMenuDensity";

/** Icon-id → component for AdminSection rows. */
const ADMIN_ICON_MAP: Record<
  string,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  key: IconKey,
  tag: IconTag,
};

/**
 * One group in the panel. Either a flat list of `MenuItem`s or the nested
 * `AdminSection` tree. Groups with no content are filtered out upstream.
 *
 * Group headers are static labels, not collapse toggles: the panel is a
 * dedicated view of one thing now (CTX-583), and a chevron on its only header
 * would just fold the panel into itself. The `AdminSection` sub-groups below
 * still collapse — that tree is deep enough to need it.
 */
export type AdminCenterPanelGroup = {
  id: string;
  title: string;
  items?: MenuItem[];
  adminSections?: AdminSection[];
};

interface AdminCenterPanelProps {
  onBack: () => void;
  onNavigate: (path: string) => void;
  activePath: string;
  groups: AdminCenterPanelGroup[];
  logoSrc: string;
  onLogoError: () => void;
  showDocumentation: boolean;
  onOpenDocumentation: () => void;
  onOpenHelp: () => void;
}

/**
 * The in-menu Admin center panel, which replaces the standard product nav on an
 * admin-center route (see `isAdminCenterShellPath`) or when its row in the
 * account card is clicked.
 *
 * It was the "Settings" drill-in until CTX-583, when the last of its other
 * groups (Profile, Notifications, Tags, Users, Customers, API, Audit Logs)
 * finished moving to the rail's own Account section and only the Admin center
 * was left. Still parameterised by `groups` rather than reading the sections
 * itself: visibility and gating are decided upstream in `SideMenu`, and this
 * renders + handles collapse/nav.
 */
export default function AdminCenterPanel({
  onBack,
  onNavigate,
  activePath,
  groups,
  logoSrc,
  onLogoError,
  showDocumentation,
  onOpenDocumentation,
  onOpenHelp,
}: AdminCenterPanelProps) {
  // Collapse state (absent key means open). The Admin center's sub-sections
  // (Resource Manager, Tenant, Kontrol Edge, …) start collapsed on mount.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    const collectAdmin = (sections: AdminSection[]) => {
      sections.forEach((section) => {
        if (section.children?.length) {
          if (!section.clickableParent) {
            initial[`admin:${section.id ?? section.label}`] = true;
          }
          collectAdmin(section.children);
        }
      });
    };
    groups.forEach((group) => {
      if (group.adminSections) collectAdmin(group.adminSections);
    });
    return initial;
  });
  const toggle = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  const isOpen = (key: string) => !collapsed[key];

  const isActive = (path?: string) =>
    !!path && (activePath === path || activePath.startsWith(path + "/"));

  /** Flat MenuItem rows (Personal / Account). */
  const renderItemRows = (items: MenuItem[]) => (
    <ul className={ROW_STACK}>
      {items.map((item) => {
        const active = isActive(item.path);
        return (
          <li key={item.id ?? item.label}>
            <button
              type="button"
              onClick={() => item.path && onNavigate(item.path)}
              className={`flex w-full items-center justify-between ${ROW_RADIUS} ${ROW_PADDING} ${ROW_TEXT} transition-colors ${
                active
                  ? "bg-semantic-state-active text-semantic-text-brand font-semibold"
                  : "text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-brand"
              }`}
            >
              <span className={`flex min-w-0 items-center ${ROW_GAP}`}>
                {item.Icon && <item.Icon className={`${ROW_ICON} shrink-0`} />}
                <span className={LABEL_CLAMP}>{item.label}</span>
              </span>
              {item.badgeBeta && (
                <span
                  className={`${BADGE_INLINE} bg-semantic-bg-brand-secondary text-semantic-text-primary`}
                >
                  {item.badgeBeta}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );

  /** Nested AdminSection rows (Admin center) — collapsible sub-groups. */
  const renderAdminSections = (sections: AdminSection[], depth = 0) => (
    <ul className={ROW_STACK}>
      {sections.map((section) => {
        const hasChildren = !!section.children?.length;
        const key = `admin:${section.id ?? section.label}`;
        const active = isActive(section.route);
        const Icon = section.icon ? ADMIN_ICON_MAP[section.icon] : undefined;
        return (
          <li key={section.id ?? section.label}>
            <div
              className={`flex cursor-pointer items-center justify-between ${ROW_RADIUS} ${ROW_PADDING} ${ROW_TEXT} transition-colors ${
                active
                  ? "bg-semantic-state-active text-semantic-text-brand font-semibold"
                  : "text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-brand"
              }`}
              onClick={() => {
                if (hasChildren && !section.clickableParent) {
                  toggle(key);
                } else if (section.route) {
                  onNavigate(section.route);
                }
              }}
            >
              <span className={`flex min-w-0 items-center ${ROW_GAP}`}>
                {Icon && <Icon className={`${ROW_ICON} shrink-0`} />}
                <span className={LABEL_CLAMP}>{section.label}</span>
              </span>
              {hasChildren && !section.clickableParent && (
                <ChevronDownSvg
                  className={`${CHEVRON_ICON} shrink-0 transition-transform ${
                    isOpen(key) ? "" : "-rotate-90"
                  }`}
                />
              )}
            </div>
            {hasChildren && (section.clickableParent || isOpen(key)) && (
              <div className={NESTED_INDENT}>
                {renderAdminSections(section.children as AdminSection[], depth + 1)}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="flex h-full flex-col bg-semantic-bg-primary">
      <div className={`flex w-full flex-col gap-2 ${CHROME_PADDING}`}>
        {!!logoSrc && (
          <img
            src={logoSrc}
            alt="Brand logo"
            className={`${DRILL_IN_LOGO} w-auto max-w-full object-contain text-semantic-text-primary`}
            onError={onLogoError}
          />
        )}
        <GenericButton
          label="Back"
          variant="secondary"
          shape="ghost"
          size="sm"
          className="flex w-full items-center justify-start text-semantic-text-secondary"
          onClick={onBack}
          icon={<IconLeft className={ROW_ICON} />}
          iconPosition="left"
        />
      </div>

      <hr className="my-2 border-semantic-border-secondary" />

      <div className={`flex-1 ${SECTION_STACK} overflow-auto ${NAV_PADDING}`}>
        {groups.map((group) => (
          <div key={group.id}>
            <div className={SECTION_HEADER_PADDING}>
              <span className={`${SECTION_HEADER_TEXT} font-semibold tracking-wide text-gray-500`}>
                {group.title}
              </span>
            </div>
            {group.adminSections
              ? renderAdminSections(group.adminSections)
              : renderItemRows(group.items ?? [])}
          </div>
        ))}
      </div>

      <div
        className={`flex flex-col ${FOOTER_STACK} border-t border-semantic-border-secondary ${FOOTER_PADDING}`}
      >
        {showDocumentation && (
          <button
            type="button"
            onClick={onOpenDocumentation}
            className={`flex items-center ${ROW_GAP} ${ROW_RADIUS} ${ROW_PADDING} ${ROW_TEXT} text-semantic-text-secondary transition-colors hover:bg-semantic-state-active hover:text-semantic-text-brand`}
          >
            <svg
              className={`${ROW_ICON} shrink-0`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
              />
            </svg>
            <span>Documentation</span>
          </button>
        )}
        <button
          type="button"
          onClick={onOpenHelp}
          className={`flex items-center ${ROW_GAP} ${ROW_RADIUS} ${ROW_PADDING} ${ROW_TEXT} text-semantic-text-secondary transition-colors hover:bg-semantic-state-active hover:text-semantic-text-brand`}
        >
          <IconSupport className={`${ROW_ICON} shrink-0`} />
          <span>Technical Support</span>
        </button>
      </div>
    </div>
  );
}
